import { Controller } from "@hotwired/stimulus"

// "Notifications on this device" on the profile page. Shows exactly one
// state block at a time and enrolls or removes THIS browser. The VAPID
// public key arrives as a Stimulus value rendered where it is consumed
// (the house idiom), the server URL likewise.
export default class extends Controller {
  static targets = ["state", "error"]
  static values = { vapidKey: String, url: String }

  connect() { this.refresh() }

  async refresh() {
    try {
      // iOS Safari has no PushManager until the site is on the Home Screen:
      // check standalone BEFORE the API check so the hint is the right one.
      if (this.ios && !this.standalone) return this.show("install")
      if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
        return this.show("unsupported")
      }
      if (Notification.permission === "denied") return this.show("denied")
      const registration = await this.registration()
      if (!registration) return this.show("unsupported")
      const subscription = await registration.pushManager.getSubscription()
      this.show(subscription ? "on" : "off")
    } catch (error) {
      // "Checking..." must never stick: a broken registration reads as
      // unsupported, and the install hint stays reachable on reload.
      this.show("unsupported")
    }
  }

  async enable() {
    // requestPermission first and synchronously inside the click: Safari
    // ties the prompt to the user gesture, and an await before it loses it.
    const permission = await Notification.requestPermission()
    if (permission !== "granted") return this.show(permission === "denied" ? "denied" : "off")
    let subscription
    try {
      // The gesture is spent; waiting for an ACTIVE worker is safe here and
      // subscribe() needs one (a first full load may still be installing).
      const registration = await navigator.serviceWorker.ready
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.serverKey()
      })
      await this.send("POST", { subscription: subscription.toJSON() })
      this.show("on")
    } catch (error) {
      // Never leave the browser subscribed with no server row: that is the
      // one state the card would misreport as "on".
      if (subscription) await subscription.unsubscribe().catch(() => {})
      this.fail("Could not turn on notifications. Please try again.")
    }
  }

  async disable() {
    try {
      const registration = await navigator.serviceWorker.getRegistration()
      const subscription = registration && await registration.pushManager.getSubscription()
      if (subscription) {
        // Browser first, then the row: the server's DELETE is idempotent, so
        // a retry heals a half-done disable in the direction that keeps
        // client and server agreeing.
        await subscription.unsubscribe()
        await this.send("DELETE", { endpoint: subscription.endpoint })
      }
      this.show("off")
    } catch (error) {
      this.fail("Could not turn off notifications. Please try again.")
    }
  }

  // getRegistration, not .ready alone: .ready never resolves when the
  // registration failed and "Checking..." would stick. On a first full
  // load the register() call in application.js may still be in flight, so
  // a null answer gets one bounded wait for .ready before it counts.
  async registration() {
    const existing = await navigator.serviceWorker.getRegistration()
    if (existing) return existing
    const timeout = new Promise((resolve) => setTimeout(() => resolve(null), 3000))
    return Promise.race([navigator.serviceWorker.ready, timeout])
  }

  // base64url (the VAPID public key) to the Uint8Array PushManager wants.
  serverKey() {
    const b64 = this.vapidKeyValue.replace(/-/g, "+").replace(/_/g, "/")
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4)
    return Uint8Array.from(atob(padded), (c) => c.charCodeAt(0))
  }

  async send(method, body) {
    const response = await fetch(this.urlValue, {
      method,
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify(body)
    })
    // A followed redirect (a staff gate bouncing to HTML) is not success.
    if (!response.ok || response.redirected) {
      throw new Error(`push subscription ${method} failed: ${response.status}`)
    }
  }

  get standalone() {
    return window.navigator.standalone === true || window.matchMedia("(display-mode: standalone)").matches
  }

  get ios() {
    return /iPhone|iPad|iPod/.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  }

  show(name) {
    this.stateTargets.forEach((el) => { el.hidden = el.dataset.state !== name })
    this.errorTarget.hidden = true
  }

  fail(message) {
    this.errorTarget.textContent = message
    this.errorTarget.hidden = false
  }
}
